// 1) Third-party libraries
import {
    all,
    call,
    delay,
    fork,
    put,
    select,
    takeLatest,
} from "redux-saga/effects";
import { fetchAuthSession } from "aws-amplify/auth";
import { get } from "lodash-es";

// 2) Local imports
// 2.1 Constants
import { REPORTS_PAGE } from "../../config/tableAndPageConstants";
import {
    API_NAME,
    maxAPIRefetchCount,
    refetchAPIDelay,
} from "../../config/config";

// 2.2 Custom hooks
// (none)

// 2.3 Project-specific modules � components/types/other (non-Redux)
import client, { post } from "src/api";
import { _queries as queries } from "../../graphql/queries.graphql";

// 2.4 Utility functions
import {
    checkShouldRequestRefetch,
    getGraphqlQueryString,
    getRegionallyApiResponse,
    getRegionallyGraphqlResponse,
    getRegionConfigFromList,
    getSortFieldsWithCustomFields,
    removeAppliedFiltersForApiRequest,
} from "../../utils/commonFunctions";
import { DynamicObject } from "../../utils/commonInterfaces";

// 2.5 Redux actions / store
import type { ApplicationState } from "../storeTypes";
import {
    getRegionKeyConfig,
    getRegionSettingsConfig,
} from "../auth/authSlice";
import { getOrganisationActionUrl } from "../organisations/organisationSlice";
import {
    downloadCompleteReportAction,
    getReportDataErrorAction,
    getReportDataForOrganisationRequestAction,
    getReportDataRequestAction,
    getReportDataSuccessAction,
    getReportsErrorAction,
    getReportsForOrganisationRequestAction,
    getReportsRequestAction,
    getReportsSuccessAction,
} from "./reportSlice";

function* handleGetReportsRequest({ payload }: any) {
    const errorMessage = 'Error fetching report list. Please try again later.';
    let { refetchCount } = get(payload, 'refetchCount') || 0;

    try {
        const {
            filters,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            excludeReports,
            Region,
            CustomerLabel
        } = payload;
        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'report',
            undefined, 
            CustomerLabel
        );
        const sortObject = getSortFieldsWithCustomFields(sortBy);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_REPORT_HISTORIES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    ...sortObject,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * REPORTS_PAGE.pageSize,
                    ExcludeReports: excludeReports
                }
            }
        );

        const { ReportHistories } = get(res.data, 'GetReportHistoriesForCompany');

        if (ReportHistories) {
            const responsePayload = {
                data: ReportHistories,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ReportHistories.length < pageSize) &&
                        !(pageSize < REPORTS_PAGE.pageSize),
                },
            };

            refetchCount = 0;
            yield put(getReportsSuccessAction(responsePayload));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getReportsRequestAction({ ...payload, refetchCount }));
        }
        else {
            yield put(getReportsErrorAction([errorMessage]));
        }
    }
}

function* handleGetReportsForOrganisationRequest({ payload }: any) {
    const errorMessage = 'Error fetching report list. Please try again later.';
    let { refetchCount } = get(payload, 'refetchCount') || 0;

    try {
        const {
            filters,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            excludeReports,
            Region
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'report'
        );
        const sortObject = getSortFieldsWithCustomFields(sortBy);
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_REPORT_HISTORIES_FOR_ORGANISATION,
            {
                ...cleanFilters,
                ...sortObject,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * REPORTS_PAGE.pageSize,
                ExcludeReports: excludeReports
            }
        );

        const { ReportHistories } = get(res.data, 'GetReportHistoriesForOrganisation');

        if (ReportHistories) {
            const responsePayload = {
                data: ReportHistories,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ReportHistories.length < pageSize) &&
                        !(pageSize < REPORTS_PAGE.pageSize),
                },
            };

            refetchCount = 0;
            yield put(getReportsSuccessAction(responsePayload));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getReportsRequestAction({ ...payload, refetchCount }));
        }
        else {
            yield put(getReportsErrorAction([errorMessage]));
        }
    }
}

/**
 * Function called for downloading the complete report.
 */
function* handleDownloadCompleteReport({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/report/complete/download',
            options: {
                body: payload
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    }
    catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleGetReportDataRequest({ payload }: any) {
    const errorMessage = 'Error fetching report details. Please try again later.';
    
    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_REPORT_DETAILS,
                variables: {
                    ReportJobId: payload,
                }
            }
        );
        
        const Report = get(res.data, 'GetReportHistoryDetails');

        if (Report) {
            const responsePayload = {
                record: Report,
            };

            yield put(getReportDataSuccessAction(responsePayload));
        }
        else {
            yield put(getReportDataErrorAction([errorMessage]));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        yield put(getReportDataErrorAction([errorMessage]));
    }
}

function* handleGetReportDataForOrganisationRequest({ payload, type }: any) {
    const errorMessage = 'Error fetching report details. Please try again later.';
    const { ReportJobId, Region } = payload;

    try {
        let apiUrl: string | undefined = undefined;

        if (Region) {
            const regionKeyConfig: DynamicObject[] = yield select(
                getRegionKeyConfig
            );
            const regionSettingsConfig: DynamicObject[] = yield select(
                getRegionSettingsConfig
            );

            const configUsed = getRegionConfigFromList(
                Region,
                regionKeyConfig,
                regionSettingsConfig
            );
            apiUrl = get(configUsed, 'Url');
        }

        let res: DynamicObject;

        const variables = {
            ReportJobId
        };

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = currentSession.tokens.accessToken;

            const response: Response = yield fetch(
                `${apiUrl}/graphql`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: accessToken,
                    },
                    body: JSON.stringify({
                        query: getGraphqlQueryString(queries.GET_REPORT_DETAILS_FOR_ORGANISATION),
                        variables,
                    }),
                }
            );

            res = yield response.json();
        }
        else {
            res = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_REPORT_DETAILS_FOR_ORGANISATION,
                    variables: variables
                }
            );
        }

        const Report = get(res.data, 'GetReportHistoryDetailsForOrganisation');

        if (Report) {
            const responsePayload = {
                record: Report,
            };

            yield put(getReportDataSuccessAction(responsePayload));
        }
        else {
            yield put(getReportDataErrorAction([errorMessage]));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        yield put(getReportDataErrorAction([errorMessage]));
    }
}

function* watchGetReportDataForOrganisationRequest() {
    yield takeLatest(
        getReportDataForOrganisationRequestAction.type,
        handleGetReportDataForOrganisationRequest
    );
}

function* watchGetReportDataRequest() {
    yield takeLatest(
        getReportDataRequestAction.type,
        handleGetReportDataRequest
    );
}

function* watchGetReportsRequest() {
    yield takeLatest(
        getReportsRequestAction.type,
        handleGetReportsRequest
    );
}

function* watchGetReportsForOrganisationRequest() {
    yield takeLatest(
        getReportsForOrganisationRequestAction.type,
        handleGetReportsForOrganisationRequest
    );
}

function* watchDownloadCompleteReportsRequest() {
    yield takeLatest(
        downloadCompleteReportAction.type,
        handleDownloadCompleteReport
    );
}

function* reportSaga() {
    yield all([
        fork(watchGetReportsRequest),
        fork(watchDownloadCompleteReportsRequest),
        fork(watchGetReportDataRequest),
        fork(watchGetReportDataForOrganisationRequest),
        fork(watchGetReportsForOrganisationRequest)
    ]);
}

export default reportSaga;
